import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import Button from "app/storybookComponents/Button";
import {
  selectAllCompanyUsersById,
  selectCurrentUserAccountId,
} from "app/containers/Global/slice";
import { UserAbsence } from "./types";
import {
  createUserAbsence,
  selectUserAbsencesByUserAccountId,
  updateUserAbsence,
  selectUpdateUserAbsenceStatus,
  selectCreateUserAbsenceStatus,
} from "./slice";
import SimpleModal from "app/components/Modals/SimpleModal";
import TextArea from "app/storybookComponents/Forms/TextArea";
import DateInput from "./DateInput";

interface Props {
  show: boolean;
  onClose: () => void;
  onConfirm: () => void;
  userAccountId?: number;
}

const OnLeaveModal = ({
  show,
  onClose,
  onConfirm,
  userAccountId,
}: Readonly<Props>) => {
  const dispatch = useAppDispatch();
  const currentUserAccountId = useAppSelector(selectCurrentUserAccountId);
  const userAbsences = useAppSelector(
    selectUserAbsencesByUserAccountId(
      userAccountId ?? currentUserAccountId ?? 0
    )
  );
  const usersById = useAppSelector(selectAllCompanyUsersById);
  const updateStatus = useAppSelector(selectUpdateUserAbsenceStatus);
  const createStatus = useAppSelector(selectCreateUserAbsenceStatus);

  const [latestAbsence, setLatestAbsence] = useState<Partial<UserAbsence>>({});

  useEffect(() => {
    if (userAbsences?.activeAbsence) {
      setLatestAbsence(userAbsences.activeAbsence);
    } else if (userAbsences?.futureAbsence) {
      setLatestAbsence(userAbsences.futureAbsence);
    } else {
      setLatestAbsence({});
    }
  }, [userAbsences]);

  const userInfo = useMemo(() => {
    return usersById[userAccountId ?? currentUserAccountId ?? 0];
  }, [userAccountId, currentUserAccountId, usersById]);

  const isEditing = useMemo(() => {
    return !!userAbsences?.activeAbsence || !!userAbsences?.futureAbsence;
  }, [userAbsences]);

  const isLoading = useMemo(
    () => (isEditing ? updateStatus === "loading" : createStatus === "loading"),
    [isEditing, updateStatus, createStatus]
  );

  const getTitle = () => {
    if (!userAccountId) return isEditing ? "Update Leave" : "Initiate Leave";
    const { firstName, lastName, emailAddress } = userInfo ?? {};
    const name = `${firstName ?? ""} ${lastName ?? ""}`.trim() ?? emailAddress;

    return isEditing
      ? `Update Leave for ${name}`
      : `Initiate Leave for ${name}`;
  };

  const getDateInputField = (
    key: "leaveStartDate" | "leaveEndDate",
    label: string,
    date?: string | Date
  ) => {
    let minDate: Date | undefined;
    if (key === "leaveEndDate") {
      const leaveStartDate = latestAbsence?.leaveStartDate
        ? new Date(latestAbsence?.leaveStartDate)
        : null;
      // If no latestAbsence?.leaveStartDate or if leaveStartDate is in the past then set minDate to today
      if (!leaveStartDate || leaveStartDate < new Date()) {
        minDate = new Date();
      } else if (leaveStartDate > new Date()) {
        minDate = leaveStartDate;
      }
    }

    return (
      <DateInput
        label={label}
        date={date}
        onChange={(date: Date | null) => {
          if (!date) return;
          const clearEndDate =
            key === "leaveStartDate" &&
            latestAbsence.leaveEndDate &&
            date.toISOString() > latestAbsence.leaveEndDate;

          setLatestAbsence({
            ...latestAbsence,
            leaveEndDate: clearEndDate ? undefined : latestAbsence.leaveEndDate,
            [key]: date.toISOString(),
          });
        }}
        minDate={minDate}
      />
    );
  };

  const onCloseWrapper = () => {
    onClose();
  };

  const onSave = async () => {
    if (!latestAbsence.leaveStartDate || !latestAbsence.leaveEndDate) return;
    const userId = userAccountId ?? currentUserAccountId ?? 0;
    if (isEditing) {
      await dispatch(
        updateUserAbsence({
          absenceId: latestAbsence.id ?? 0,
          leaveStartDate: latestAbsence.leaveStartDate,
          leaveEndDate: latestAbsence.leaveEndDate,
          leaveReason: latestAbsence.leaveReason ?? "",
          wasCancelled: false,
          userAccountId: userId,
        })
      );
    } else {
      await dispatch(
        createUserAbsence({
          userAccountId: userId,
          leaveStartDate: latestAbsence.leaveStartDate,
          leaveEndDate: latestAbsence.leaveEndDate,
          leaveReason: latestAbsence.leaveReason ?? "",
        })
      );
    }
    onCloseWrapper();
  };

  const onCancelExistingLeave = async () => {
    if (!latestAbsence.id) return;
    const userId = userAccountId ?? currentUserAccountId ?? 0;
    await dispatch(
      updateUserAbsence({
        userAccountId: userId,
        absenceId: latestAbsence.id,
        wasCancelled: true,
      })
    );
    onCloseWrapper();
  };

  return (
    <SimpleModal show={show} onHide={onCloseWrapper} title={getTitle()}>
      <div className="row-gap-12px">
        {getDateInputField(
          "leaveStartDate",
          "Start Date",
          latestAbsence?.leaveStartDate
        )}
        {getDateInputField(
          "leaveEndDate",
          "End Date",
          latestAbsence?.leaveEndDate
        )}
      </div>
      <TextArea
        inputText={latestAbsence.leaveReason ?? ""}
        inputLabel="Reason to Leave"
        controlId="reasonToLeave"
        onTextChange={(text) =>
          setLatestAbsence({ ...latestAbsence, leaveReason: text })
        }
        maxLength={255}
        placeholder="Enter reason to leave..."
      />
      <div className="d-flex justify-content-between">
        <div>
          {isEditing ? (
            <Button
              onClick={onCancelExistingLeave}
              variant="secondary-danger"
              disabled={isLoading}
            >
              Cancel Leave
            </Button>
          ) : null}
        </div>
        <div className="row-gap-12px">
          <Button
            onClick={onCloseWrapper}
            variant="secondary-blue"
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={onSave}
            disabled={!latestAbsence.leaveStartDate || isLoading}
          >
            {isEditing ? "Update" : "Submit"}
          </Button>
        </div>
      </div>
    </SimpleModal>
  );
};

export default OnLeaveModal;
